import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { useAuthStore } from '../stores/auth.store';

// Laravel Echo's Reverb driver uses the Pusher protocol internally.
// It requires Pusher to be available as a global — this is the standard pattern.
// Pusher isn't a DOM-standard global, so widen `window` with a typed slot
// rather than casting through `any`. The two-step cast (through `unknown`)
// satisfies TS's "neither type sufficiently overlaps" guard.
(window as unknown as { Pusher: typeof Pusher }).Pusher = Pusher;

const API_URL = (import.meta.env.VITE_API_URL as string) || '/api/v1';

/**
 * Resolve the broadcasting auth endpoint.
 * Broadcasting auth lives at /broadcasting/auth (NOT under /api/v1).
 */
function getAuthEndpoint(): string {
  if (API_URL.startsWith('/')) {
    return '/broadcasting/auth';
  }
  const url = new URL(API_URL);
  return `${url.origin}/broadcasting/auth`;
}

let echoInstance: Echo<'reverb'> | null = null;

/**
 * Get or create the Echo instance.
 * Uses a factory (not module-level singleton) so the auth token is read fresh
 * from the store on each channel subscription, not captured at construction time.
 */
export function getEcho(): Echo<'reverb'> {
  if (echoInstance) return echoInstance;

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: import.meta.env.VITE_REVERB_APP_KEY || 'empathease-key',
    wsHost: import.meta.env.VITE_REVERB_HOST || window.location.hostname,
    wsPort: Number(import.meta.env.VITE_REVERB_PORT) || 6001,
    wssPort: Number(import.meta.env.VITE_REVERB_PORT) || 6001,
    forceTLS: import.meta.env.PROD,
    enabledTransports: ['ws', 'wss'],
    authEndpoint: getAuthEndpoint(),
    auth: {
      headers: {
        // Dynamic getter — reads current token on each auth request, not at construction time
        get Authorization() {
          const token = useAuthStore.getState().token;
          return token ? `Bearer ${token}` : '';
        },
        Accept: 'application/json',
      },
    },
  });

  return echoInstance;
}

/**
 * Return the existing Echo instance without creating one.
 * Used by api.ts interceptor to avoid eagerly constructing Echo on unauthenticated requests.
 */
export function getEchoIfExists(): Echo<'reverb'> | null {
  return echoInstance;
}

/**
 * Destroy the Echo instance (call on logout).
 */
export function destroyEcho(): void {
  if (echoInstance) {
    echoInstance.disconnect();
    echoInstance = null;
  }
}

/**
 * Check if WebSocket mode is enabled via feature flag.
 */
export function isWsEnabled(): boolean {
  return import.meta.env.VITE_WS_ENABLED === 'true';
}
